<template>
	<div class="coupons-container">
		<AppCard class="classic-card" :title="getString('coupon_codes')">
			<template #body>
				<AppList :items="itemsCoupons">
					<template #coupon-slot="{item}">
						<div class="row centered space-between row-clickable chevron-slot" @click="clickOpenOnlyOne(item)">
							<p class="list-item-title">{{ item.title }}</p>
							<div class="coupon-info-container">
								<div class="value">{{ getCouponValue(item) }}</div>
								<div class="usages" :class="{used: item.max_redeems && Number(item.max_redeems) === item.orders_count}">{{ item.orders_count + (item.max_redeems ? '/' + item.max_redeems : '') }}</div>
								<AppChevronIcon :direction="item.active ? 'up' : 'down'"/>
							</div>
						</div>
					</template>
					<template #coupon-dropdown="{item}">
						<AppList :items="couponItems(item)">
							<template #btn-slot="{item}">
								<div class="row centered space-between text-slot">
									<p class="list-item-title">{{ item.title }}</p>
									<AppButton
										outline small
										:disabled="item.loading"
										@click.stop="item.callback"
										:text="getString(item.loading ? 'loading' : 'edit')"
										class="button-btn-slot"
									/>
								</div>
							</template>
							<template #text-slot="{item}">
								<div class="row centered space-between">
									<p class="list-item-title">{{ item.title }}</p>
									<div class="list-item-value selectable">{{ item.value }}</div>
								</div>
							</template>
							<template #switches-slot="{item}">
								<div class="switches-container">
									<AppSwitch v-for="sw in item.switches" :key="sw.key" disabled :label="sw.label" :checked="sw.active"/>
								</div>
							</template>
							<template #linked-list-slot="{item}">
								<div class="row linked-list-container dropdown-content">
									<div class="linked-list-item" v-for="(listItem, index) in item.list" :key="index">{{ listItem }}</div>
								</div>
							</template>
							<template #delete-coupon-slot="{item}">
								<div class="row flex-end">
									<AppButton @click="deleteCoupon(item)" :loading="item.loadingDelete" class="ml-auto" :text="getString('delete')" small outline/>
								</div>
							</template>
						</AppList>
					</template>
					<template #new-coupon-slot="{item}">
						<div class="row centered space-between row-clickable chevron-slot new-coupon-row" @click="item.callback">
							<div class="new-coupon-title">
								<div class="icon-plus" v-html="getIcon('iconPlus')"></div>
								<p class="list-item-title">{{ item.title }}</p>
							</div>
							<div class="chevron-container">
								<AppChevronIcon :direction="item.active ? 'up' : 'down'"/>
							</div>
						</div>
					</template>
					<template #new-coupon-dropdown="{item}">
						<AppList :items="newCouponItems">
							<template #btn-slot="{item}">
								<div class="row centered space-between text-slot">
									<p class="list-item-title">{{ item.title }}</p>
									<AppButton
										outline small
										@click.stop="item.callback"
										:text="getString('edit')"
										class="button-btn-slot"
									/>
								</div>
							</template>
							<template #text-slot="{item}">
								<div class="row centered space-between text-slot">
									<p class="list-item-title">{{ item.title }}</p>
									<AppTextInput class="ml-auto" :ref="item.ref" :placeholder="item.placeholder" @changed-text="item.callback" @focusout-input="item.focusout ? item.focusout() : null"/>
								</div>
							</template>
							<template #coupon-value-slot="{item}">
								<div class="row centered space-between text-slot">
									<p class="list-item-title">{{ item.title }}</p>
									<div class="coupon-value-switch ml-auto">
										<AppSwitch class="mr-10" :checked="newCouponPercent" left-icon="iconCash" right-icon="iconPercent" :fixed-color="true"
												   @switched="newCouponPercent = !newCouponPercent"/>
										<AppNumberInput :ref="item.ref" double-decimal :min="0" :max="newCouponPercent ? 100 : null"
														:label="label" right-side-symbol/>
									</div>
								</div>
							</template>
							<template #quantity-slot="{item}">
								<div class="row flex-end">
									<AppNumberInput :value="item.value" :min="item.min ? item.min : 0"
													@changed-value="item.callbackQuantity" :symbol="item.symbol"
													:right-side-symbol="item.symbolRight"/>
								</div>
							</template>
							<template #coupon-mode-slot="{item}">
								<div class="coupon-mode-container">
									<p class="list-item-title">{{ item.title }}</p>
									<AppTabsSwitch :items="item.tabItems" :active-item="newCouponModeTab" @switched="item.callback"/>
								</div>
							</template>
							<template #switch-slot="{item}">
								<div class="row centered space-between dropdown-content">
									<p class="list-item-title">{{ item.titleSwitch }}</p>
									<AppSwitch :checked="item.activeSwitch" @switched="item.callbackSwitch"/>
								</div>
							</template>
							<template #expire-slot="{item}">
								<div class="row flex-end">
									<DatePicker ref="date-picker" v-model="newCouponExpireDate" mode="datetime" is24hr color="teal"
												:min-date='new Date()' :is-dark="userStore.darkMode">
										<template #default="{ togglePopover }">
											<AppTextInput class="text-input" @click="togglePopover()"
														  :value="newCouponExpireDate ? formatDateMMDDHHmm(newCouponExpireDate) : ''" readonly/>
										</template>
									</DatePicker>
								</div>
							</template>
							<template #products-slot="{item}">
								<div class="row flex-end align-center">
									<p class="color-txt-1 mr-10">{{getString('x_selected', [newCouponProductsIds.length])}}</p>
									<AppButton :text="getString('select_products')" outline @click="showProductPicker = true"/>
								</div>
							</template>
							<template #categories-slot="{item}">
								<div class="row flex-end align-center">
									<p class="color-txt-1 mr-10">{{getString('x_categories', [newCouponCategoriesIds.length])}}</p>
									<AppButton :text="getString('select_categories')" outline @click="showCategoryPicker = true"/>
								</div>
							</template>
							<template #rooms-slot="{item}">
								<div class="row flex-end align-center">
									<p class="color-txt-1 mr-10">{{getString('x_rooms', [newCouponRoomsIds.length])}}</p>
									<AppButton :text="getString('select_rooms')" outline @click="showRoomPicker = true"/>
								</div>
							</template>
							<template #save-coupon-slot="{item}">
								<div class="row flex-end">
									<AppButton @click="addCoupon" :loading="loadingSaveCoupon" class="ml-auto"
											   :text="getString('add_coupon')" small/>
								</div>
							</template>
						</AppList>
					</template>
				</AppList>
			</template>
		</AppCard>

		<AppBottomSheet :active="showProductPicker" :force-width="bottomSheetWidth"
						@toggling-popup="active => toggleBottomSheet('product', active)">
			<ModalHeader :title="getString('select_products')" :button-text="getString('apply')"
						 @click-button="toggleBottomSheet('product', false)" />
			<div class="force-width">
				<AppTextInput :placeholder="getString('search')" icon-name="icon-magnify" :value="pickerSearchKey"
							  @changed-text="updatePickerSearchKey"/>
			</div>
			<AppList :items="allProducts"/>
		</AppBottomSheet>
		<AppBottomSheet :active="showCategoryPicker" :force-width="bottomSheetWidth"
						@toggling-popup="active => toggleBottomSheet('category', active)">
			<ModalHeader :title="getString('select_categories')" :button-text="getString('apply')"
						 @click-button="toggleBottomSheet('category', false)" />
			<div class="force-width">
				<AppTextInput :placeholder="getString('search')" icon-name="icon-magnify" :value="pickerSearchKey"
							  @changed-text="updatePickerSearchKey"/>
			</div>
			<AppList :items="allCategories"/>
		</AppBottomSheet>
		<AppBottomSheet :active="showRoomPicker" :force-width="bottomSheetWidth"
						@toggling-popup="active => toggleBottomSheet('room', active)">
			<ModalHeader :title="getString('select_rooms')" :button-text="getString('apply')"
						 @click-button="toggleBottomSheet('room', false)" />
			<div class="force-width">
				<AppTextInput :placeholder="getString('search')" icon-name="icon-magnify" :value="pickerSearchKey"
							  @changed-text="updatePickerSearchKey"/>
			</div>
			<AppList :items="allRooms"/>
		</AppBottomSheet>
		<AppBottomSheet :active="showVerificationCodesSheet" :force-width="bottomSheetWidth"
						@toggling-popup="active => toggleBottomSheet('verification', active)">
			<VerificationCodesManager
				v-if="currentCouponForVerification"
				:new-coupon="!currentCouponForVerification?.business_coupon_id"
				:initial-codes="currentCouponForVerification?.verification_codes || []"
				@save-codes="saveVerificationCodes"
			/>
		</AppBottomSheet>
	</div>
</template>

<script>
import AppList from "@/shared/components/AppList.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AppCard from "@/shared/components/AppCard.vue";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import {useCurrencyStore, useLanguageStore, useNotificationStore, useSvgStore} from "@/shared/stores";
import {useCouponsStore, useMenuStore, useRoomsStore, useUserStore} from "@/console-new/stores";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";
import AppTabsSwitch from "@/shared/components/AppTabsSwitch.vue";
import {formatDateMMDDHHmm, formatDateTime, getLocalDateFromServer} from "@/shared/helpers/dates";
import AppChecklist from "@/shared/components/AppChecklist.vue";
import API from "@/shared/helpers/API";
import {getRandomAlphanumericString} from "@/shared/helpers/strings";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import VerificationCodesManager from "@/console-new/components/config/coupons/VerificationCodesManager.vue";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let userStore = useUserStore();
		const notificationStore = useNotificationStore();
		const svgStore = useSvgStore();
		const menuStore = useMenuStore();
		const couponsStore = useCouponsStore();
		const roomsStore = useRoomsStore();
		const currencyStore = useCurrencyStore();
		return {
			getString: languageStore.getString,
			showNotification: notificationStore.showNotification,
			userStore,
			currencyStore,
			getIcon: svgStore.get,
			menuStore,
			couponsStore,
			roomsStore
		}
	},
	name: "CouponsContainer",
	components: {
		ModalHeader,
		AppChecklist,
		AppSwitch,
		AppTabsSwitch,
		AppNumberInput,
		AppTextInput,
		AppChevronIcon,
		AppCard,
		AppButton,
		AppList,
		AppBottomSheet,
		VerificationCodesManager
	},
	mixins: [WindowWidth],
	data() {
		return {
			newCouponActive: false,
			newCouponPercent: true,
			newCouponMaxRedeemsActive: false,
			newCouponMaxRedeemsValue: 1,
			newCouponMinimumOrderActive: false,
			newCouponMinimumOrderValue: 0,
			newCouponCumulative: true,
			newCouponOncePerUser: false,
			newCouponFirstTimeOnly: false,
			newCouponDelivery: true,
			newCouponTakeaway: true,
			newCouponModeTab: 0,
			newCouponExpireDateActive: false,
			newCouponExpireDate: new Date(),
			newCouponProductsActive: false,
			newCouponProductsIds: [],
			newCouponCategoriesActive: false,
			newCouponCategoriesIds: [],
			newCouponRoomsActive: false,
			newCouponRoomsIds: [],
			newCouponVerificationCodes: [],
			loadingSaveCoupon: false,
			loadingDelete: false,
			showProductPicker: false,
			showCategoryPicker: false,
			showRoomPicker: false,
			pickerSearchKey: null,
			showVerificationCodesSheet: false,
			currentCouponForVerification: null,
		}
	},
	computed: {
		itemsCoupons() {
			let coupons = this.couponsStore.businessCoupons ? [...this.couponsStore.businessCoupons] : [];
			coupons = coupons.sort((a, b) => {
				return a.title.localeCompare(b.title);
			});
			coupons.push({
				title: this.getString("new_coupon"),
				active: this.newCouponActive,
				customSlot: 'new-coupon-slot',
				dropdownSlot: 'new-coupon-dropdown',
				callback: () => {
					this.newCouponActive = !this.newCouponActive;
					this.closeAllCoupons();
				},
			});
			return coupons;
		},
		newCouponItems() {
			let items = [
				{
					title: this.getString("name"),
					customSlot: 'text-slot',
					ref: 'new-coupon-name',
					placeholder: this.getString("coupon_name"),
				},
				{
					title: this.getString("coupon_code"),
					customSlot: 'text-slot',
					ref: 'new-coupon-code',
					placeholder: this.getString("coupon_code"),
					focusout: () => {
						this.$refs['new-coupon-code'].currentValue = this.sanitizeCouponCode(this.$refs['new-coupon-code'].currentValue);
					},
				},
				{
					title: this.getString("coupon_value"),
					customSlot: 'coupon-value-slot',
					ref: 'new-coupon-value',
					placeholder: this.getString("coupon_code"),
				},
				{
					title: this.getString("expire_date"),
					switch: true,
					active: this.newCouponExpireDateActive,
					callback: (newStatus) => {
						this.newCouponExpireDateActive = newStatus;
					},
					dropdownSlot: 'expire-slot',
					highZ: true,
				},
				{
					title: this.getString("max_redeems"),
					switch: true,
					active: this.newCouponMaxRedeemsActive,
					callback: (newStatus) => {
						this.newCouponMaxRedeemsActive = newStatus;
						this.newCouponMaxRedeemsValue = 1;
					},
					dropdownSlot: 'quantity-slot',
					value: this.newCouponMaxRedeemsValue,
					min: 1,
					symbol: null,
					symbolRight: false,
					callbackQuantity: (newStatus) => {
						this.newCouponMaxRedeemsValue = newStatus;
					},
				},
				{
					title: this.getString("minimum_order"),
					switch: true,
					active: this.newCouponMinimumOrderActive,
					callback: (newStatus) => {
						this.newCouponMinimumOrderActive = newStatus;
						this.newCouponMinimumOrderValue = 0;
					},
					dropdownSlot: 'quantity-slot',
					value: this.newCouponMinimumOrderValue,
					symbol: this.currencyStore.currCurrency.symbol,
					symbolRight: true,
					callbackQuantity: (newStatus) => {
						this.newCouponMinimumOrderValue = newStatus;
					},
				},
				{
					title: this.getString("once_per_user"),
					switch: true,
					active: this.newCouponOncePerUser,
					callback: (newStatus) => {
						this.newCouponOncePerUser = newStatus;
						this.newCouponFirstTimeOnly = false;
					},
					dropdownSlot: 'switch-slot',
					activeSwitch: this.newCouponFirstTimeOnly,
					callbackSwitch: (newStatus) => {
						this.newCouponFirstTimeOnly = newStatus;
					},
					titleSwitch: this.getString("first_order_only"),
				},
				{
					title: this.getString("is_cumulative"),
					switch: true,
					active: this.newCouponCumulative,
					callback: (newStatus) => {
						this.newCouponCumulative = newStatus;
					},
				},
				{
					title: ' ',
					customSlot: 'coupon-mode-slot',
					tabItems: [this.getString("qr_orders"), this.getString("pay_the_bill")],
					callback: (index) => {
						this.newCouponModeTab = index;
						if (index === 1) {
							this.newCouponDelivery = false;
							this.newCouponTakeaway = false;
							this.newCouponProductsActive = false;
							this.newCouponProductsIds = [];
							this.newCouponCategoriesActive = false;
							this.newCouponCategoriesIds = [];
						} else {
							this.newCouponDelivery = true;
							this.newCouponTakeaway = true;
						}
					},
				}
			];
			if (this.newCouponModeTab === 0) {
				if (this.userStore.currPlans.delivery) {
					items.push({
						uniqueId: 'coupon-delivery',
						title: this.getString("valid_for_delivery"),
						switch: true,
						active: this.newCouponDelivery,
						callback: (newStatus) => {
							if (!newStatus && !this.newCouponTakeaway) {
								this.showNotification('coupon-mode');
								return;
							}
							this.newCouponDelivery = newStatus;
						},
					});
				}
				if (this.userStore.currPlans.takeaway) {
					items.push({
						uniqueId: 'coupon-takeaway',
						title: this.getString("valid_for_takeaway"),
						switch: true,
						active: this.newCouponTakeaway,
						callback: (newStatus) => {
							if (!newStatus && !this.newCouponDelivery) {
								this.showNotification('coupon-mode');
								return;
							}
							this.newCouponTakeaway = newStatus;
						},
					});
				}
				items.push({
					uniqueId: 'coupon-products',
					title: this.getString("specific_products"),
					dropdownSlot: 'products-slot',
					switch: true,
					active: this.newCouponProductsActive,
					callback: (newStatus) => {
						this.newCouponProductsActive = newStatus;
						this.newCouponProductsIds = [];
					},
				});
				items.push({
					uniqueId: 'coupon-categories',
					title: this.getString("specific_categories"),
					dropdownSlot: 'categories-slot',
					switch: true,
					active: this.newCouponCategoriesActive,
					callback: (newStatus) => {
						this.newCouponCategoriesActive = newStatus;
						this.newCouponCategoriesIds = [];
					},
				});
			}
			items.push({
				uniqueId: 'coupon-rooms',
				title: this.getString("specific_rooms"),
				dropdownSlot: 'rooms-slot',
				switch: true,
				active: this.newCouponRoomsActive,
				callback: (newStatus) => {
					this.newCouponRoomsActive = newStatus;
					this.newCouponRoomsIds = [];
				},
			});
			items.push({
				title: this.getString('additional_verification_codes'),
				customSlot: 'btn-slot',
				callback: () => this.openVerificationCodes(this.currNewCoupon),
			});
			items.push({
				title: this.getString("save"),
				customSlot: 'save-coupon-slot',
			});
			return items;
		},
		allProducts() {
			let products = [];
			Object.values(this.menuStore.items).forEach((item) => {
				if (item.deleted_item || this.isItemFiltered(item)) {
					return;
				}
				products.push({
					key: item.menu_item_id,
					title: item.name,
					switch: true,
					active: this.newCouponProductsIds.indexOf(item.menu_item_id) !== -1,
					callback: active => this.newCouponProductsChanged(active, item.menu_item_id)
				});
			});
			return products;
		},
		allCategories() {
			let categories = [];
			Object.values(this.menuStore.categories).forEach((category) => {
				if (this.isItemFiltered(category)) {
					return;
				}
				categories.push({
					title: category.name,
					switch: true,
					active: this.newCouponCategoriesIds.indexOf(category.menu_category_id) !== -1,
					callback: active => this.newCouponCategoriesChanged(active, category.menu_category_id)
				});
			});
			return categories;
		},
		allRooms() {
			let rooms = [];
			(this.roomsStore.rooms ?? []).forEach((room) => {
				if (this.isItemFiltered(room)) {
					return;
				}
				rooms.push({
					title: room.name,
					switch: true,
					active: this.newCouponRoomsIds.indexOf(room.room_id) !== -1,
					callback: active => this.newCouponRoomsChanged(active, room.room_id)
				});
			});
			return rooms;
		},
		label() {
			return this.getString('price_symbol', [this.newCouponPercent ? '%' : this.currencyStore.currCurrency.symbol]);
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		},
		currNewCoupon() {
			let targetCoupon = this.itemsCoupons.find(c => !c.business_coupon_id);
			return targetCoupon || {};
		}
	},
	methods: {
		formatDateMMDDHHmm,
		sanitizeCouponCode(code) {
			let newCode = code.replace(/[^a-zA-Z0-9]/g, '');
			newCode = newCode.toUpperCase();
			let collision = this.couponsStore.businessCoupons.find((coupon) => {
				return coupon.code === newCode;
			});
			if(collision) {
				newCode += getRandomAlphanumericString(4);
				newCode = newCode.toUpperCase();
			}
			return newCode;
		},
		clickOpenOnlyOne(coupon) {
			this.closeAllCoupons(coupon.business_coupon_id);
			coupon.active = !coupon.active;
			this.newCouponActive = false;
			if(!!coupon.active && !coupon?.verification_codes?.length) {
				coupon.loadingCodes = true;
				this.couponsStore.getCouponVerificationCodes(coupon.business_coupon_id, (codes) => {
					coupon.loadingCodes = false;
				});
			}
		},
		closeAllCoupons(exceptId = null) {
			this.itemsCoupons.forEach((coupon) => {
				if (coupon.customSlot !== 'new-coupon-slot' && coupon.business_coupon_id !== exceptId) {
					coupon.active = false;
				}
			});
		},
		newCouponProductsChanged(active, index) {
			if (active) {
				this.newCouponProductsIds.push(index)
			}
			else {
				this.newCouponProductsIds.splice(this.newCouponProductsIds.indexOf(index), 1);
			}
		},
		newCouponCategoriesChanged(active, index) {
			if (active) {
				this.newCouponCategoriesIds.push(index)
			}
			else {
				this.newCouponCategoriesIds.splice(this.newCouponCategoriesIds.indexOf(index), 1);
			}
		},
		newCouponRoomsChanged(active, index) {
			if (active) {
				this.newCouponRoomsIds.push(index)
			}
			else {
				this.newCouponRoomsIds.splice(this.newCouponRoomsIds.indexOf(index), 1);
			}
		},
		addCoupon() {

			let name = this.$refs['new-coupon-name'].currentValue;
			let code = this.$refs['new-coupon-code'].currentValue;
			let value = this.$refs['new-coupon-value'].currentValue;
			if (!name || name.length <= 2) {
				this.showNotification(this.getString('coupon-missing-name'), 'error');
				return;
			}
			if (!code || code.length <= 3) {
				this.showNotification(this.getString('coupon-missing-code'), 'error');
				return;
			}
			if (!value || value <= 0) {
				this.showNotification(this.getString('coupon-missing-value'), 'error');
				return;
			}
			let fixedValue = this.newCouponPercent ? null : this.currencyStore.actualToDbValue(value);
			let percentValue = this.newCouponPercent ? value : null;
			let maxRedeems = this.newCouponMaxRedeemsActive ? this.newCouponMaxRedeemsValue : null;
			let minOrderValue = this.newCouponMinimumOrderActive ? this.currencyStore.actualToDbValue(this.newCouponMinimumOrderValue) : null;
			let canStack = this.newCouponCumulative ? 1 : 0;
			let oncePerUser = this.newCouponOncePerUser ? 1 : 0;
			let firstTimeOnly = this.newCouponFirstTimeOnly ? 1 : 0;
			let delivery = this.newCouponDelivery ? 1 : 0;
			let takeaway = this.newCouponTakeaway ? 1 : 0;
			let qrMode = this.newCouponModeTab === 0 ? 1 : 0;
			let paytableMode = this.newCouponModeTab === 1 ? 1 : 0;
			let linkedProducts = this.newCouponProductsActive ? JSON.stringify(this.newCouponProductsIds) : null;
			let linkedCategories = this.newCouponCategoriesActive ? JSON.stringify(this.newCouponCategoriesIds) : null;
			let linkedRooms = this.newCouponRoomsActive ? JSON.stringify(this.newCouponRoomsIds) : null;
			let expireDate = this.newCouponExpireDateActive ? formatDateTime(this.newCouponExpireDate) : null;
			let verificationCodes = this.newCouponVerificationCodes?.length ? this.newCouponVerificationCodes : null;

			this.loadingSaveCoupon = true;
			API.init().addCoupon(this.userStore.currBusinessId, name, code, fixedValue, percentValue, maxRedeems, minOrderValue, canStack, oncePerUser, firstTimeOnly, qrMode, paytableMode, delivery, takeaway, linkedProducts, linkedCategories, linkedRooms, expireDate, verificationCodes, (r) => {
				if (r && r.result && r.result === "OK") {
					this.$refs['new-coupon-name'].currentValue = "";
					this.$refs['new-coupon-code'].currentValue = "";
					this.$refs['new-coupon-value'].currentValue = "0";
					this.newCouponPercent = true;
					this.newCouponMaxRedeemsActive = false;
					this.newCouponMaxRedeemsValue = 1;
					this.newCouponMinimumOrderActive = false;
					this.newCouponMinimumOrderValue = 0;
					this.newCouponCumulative = true;
					this.newCouponOncePerUser = false;
					this.newCouponFirstTimeOnly = false;
					this.newCouponDelivery = true;
					this.newCouponTakeaway = true;
					this.newCouponModeTab = 0;
					this.newCouponExpireDateActive = false;
					this.newCouponExpireDate = new Date();
					this.newCouponProductsActive = false;
					this.newCouponProductsIds = [];
					this.newCouponCategoriesActive = false;
					this.newCouponCategoriesIds = [];
					this.newCouponRoomsActive = false;
					this.newCouponRoomsIds = [];
					this.newCouponVerificationCodes = [];
					this.couponsStore.getBusinessCoupons(() => {
						this.newCouponActive = false;
						this.loadingSaveCoupon = false;
						this.showNotification("generic-success");
					}, true);

				} else if(r.details && r.details === "code_already_exists") {
					this.loadingSaveCoupon = false;
					this.showNotification("coupon-code-exists");
				} else if(r.details && r.details === "invalid_code") {
					this.loadingSaveCoupon = false;
					this.showNotification("coupon-code-invalid");
				} else if(r.details && r.details === "invalid_mode") {
					this.loadingSaveCoupon = false;
					this.showNotification("coupon-mode");
				} else {
					this.loadingSaveCoupon = false;
					this.showNotification("generic-fail");
				}
			});

		},
		getCouponValue(coupon) {
			if (coupon.fixed_value) {
				return this.currencyStore.readableCurrencyValue(coupon.fixed_value);
			} else {
				let percentageMaxTwoDecimals = Math.floor(coupon.percentage_value * 100) / 100;
				return percentageMaxTwoDecimals + "%";
			}
		},
		couponItems(coupon) {
			let items = [
				{
					title: this.getString("coupon_code"),
					customSlot: 'text-slot',
					value: coupon.code,
				},
			];
			if (coupon.date_expire) {
				items.push({
					title: this.getString("expire_date"),
					customSlot: 'text-slot',
					value: formatDateMMDDHHmm(getLocalDateFromServer(coupon.date_expire)),
				});
			}
			if (coupon.minimum_order_value) {
				items.push({
					title: this.getString("minimum_order"),
					customSlot: 'text-slot',
					value: this.currencyStore.readableCurrencyValue(coupon.minimum_order_value),
				});
			}
			if (coupon.linked_products) {
				let listProducts = [];
				coupon.products.forEach((product) => {
					if (this.menuStore.items.hasOwnProperty(product.item_id)) {
                        listProducts.push(this.menuStore.items[product.item_id].name);
					}
				});
                if(listProducts.length > 0) {
                    items.push({
                        title: this.getString("x_products", [listProducts.length]),
						chevronItem: true,
                        dropdownSlot: 'linked-list-slot',
                        active: coupon.productsDropdownActive,
                        list: listProducts,
                        callback: () => {
                            coupon.productsDropdownActive = !coupon.productsDropdownActive;
                            coupon.categoriesDropdownActive = false;
                            coupon.roomsDropdownActive = false;
                        },
                    });
                }


                let listCategories = [];
                coupon.categories.forEach((category) => {
                    if (this.menuStore.categories.hasOwnProperty(category.category_id)) {
                        listCategories.push(this.menuStore.categories[category.category_id].name);
                    }
                });
                if(listCategories.length > 0) {
                    items.push({
                        title: this.getString("x_categories", [listCategories.length]),
						chevronItem: true,
                        dropdownSlot: 'linked-list-slot',
                        active: coupon.categoriesDropdownActive,
                        list: listCategories,
                        callback: () => {
                            coupon.categoriesDropdownActive = !coupon.categoriesDropdownActive;
                            coupon.productsDropdownActive = false;
                            coupon.roomsDropdownActive = false;
                        },
                    });
                }
			}
			if(coupon.linked_rooms) {
				let list = [];
				if(this.roomsStore.rooms) {
					coupon.rooms.forEach((room) => {
						let found = this.roomsStore.rooms.find(r => r.room_id === Number(room.room_id));
						if (found) {
							list.push(found.name);
						}
					});
				}
				items.push({
					title: this.getString("x_rooms", [list.length]),
					chevronItem: true,
					dropdownSlot: 'linked-list-slot',
					active: coupon.roomsDropdownActive,
					list: list,
					callback: () => {
						coupon.roomsDropdownActive = !coupon.roomsDropdownActive;
						coupon.productsDropdownActive = false;
						coupon.categoriesDropdownActive = false;
					},
				});
			}
			let switches = [
				{
					key: 'canStack',
					label: this.getString("is_cumulative"),
					active: coupon.can_stack,
				}, {
					key: 'oncePerUser',
					label: this.getString("once_per_user"),
					active: coupon.once_per_user,
				}, {
					key: 'firstTimeOnly',
					label: this.getString("first_order_only"),
					active: coupon.first_time_only,
				}
			];
			switches.push({
				key: 'qr_mode',
				label: this.getString("qr_orders"),
				active: coupon.qr_mode,
			});
			switches.push({
				key: 'paytable_mode',
				label: this.getString("pay_the_bill"),
				active: coupon.paytable_mode,
			});
			if (this.userStore.currPlans.delivery) {
				switches.push({
					key: 'delivery',
					label: this.getString("valid_for_delivery"),
					active: coupon.delivery,
				});
			}
			if (this.userStore.currPlans.takeaway) {
				switches.push({
					key: 'takeaway',
					label: this.getString("valid_for_takeaway"),
					active: coupon.takeaway,
				});
			}
			items.push({
				title: '',
				customSlot: 'switches-slot',
				switches: switches,
			});
			if(coupon?.verification_codes?.length) {
				items.push({
					title: this.getString('additional_verification_codes'),
					customSlot: 'btn-slot',
					loading: coupon.loadingCodes,
					value: coupon.verification_codes ? coupon.verification_codes.length + ' ' + this.getString('codes') : this.getString('no_codes'),
					callback: () => this.openVerificationCodes(coupon),
				});
			}
			items.push({
				title: this.getString("delete"),
				customSlot: 'delete-coupon-slot',
				loadingDelete: this.loadingDelete,
				business_coupon_id: coupon.business_coupon_id,
			});
			return items;
		},
		deleteCoupon(coupon) {
			this.loadingDelete = true;
			API.init().deleteCoupon(this.userStore.currBusinessId, coupon.business_coupon_id, (r) => {
				if (r && r.result && r.result === "OK") {
					this.couponsStore.getBusinessCoupons(() => {
						this.loadingDelete = false;
						this.showNotification("generic-success");
					}, true);
				} else {
					this.loadingDelete = false;
					this.showNotification("generic-fail");
				}
			});
		},
		toggleBottomSheet(sheet, active) {
			if(!active) {
				if(sheet === 'product') {
					this.showProductPicker = false;
				} else if(sheet === 'category') {
					this.showCategoryPicker = false;
				} else if(sheet === 'room') {
					this.showRoomPicker = false;
				} else if(sheet === 'verification') {
					this.showVerificationCodesSheet = false;
					this.currentCouponForVerification = null;
				}
			}
			this.$nextTick(() => this.pickerSearchKey = null);
		},
		updatePickerSearchKey(key) {
			this.pickerSearchKey = key.toLowerCase();
		},
		isItemFiltered(item) {
			return this.pickerSearchKey && item.name && !item.name.toLowerCase().includes(this.pickerSearchKey);
		},
		openVerificationCodes(coupon) {
			if (!coupon?.business_coupon_id) {
				coupon = { ...coupon, verification_codes: this.newCouponVerificationCodes };
			}
			this.currentCouponForVerification = coupon;
			this.showVerificationCodesSheet = true;
		},
		saveVerificationCodes(codes) {
			let targetCoupon = this.currentCouponForVerification;
			if (!targetCoupon?.business_coupon_id) {
				this.newCouponVerificationCodes = codes;
			} else {
				let startingArray = targetCoupon?.verification_codes || [];
				let newArray = [];
				let stringsArray = [];
				(codes || []).forEach(cod => {
					let prevUsage = startingArray.find(c => c.verification_code === cod)?.usages || 0;
					newArray.push({ verification_code: cod, usages: prevUsage })
					stringsArray.push(cod)
				})
				targetCoupon.verification_codes = newArray;
				this.couponsStore.updateCouponVerificationCodes(targetCoupon.business_coupon_id, stringsArray, success => {
					this.showNotification("success-saved-x-coupon-codes", [stringsArray?.length || 0]);
				});
			}

			this.showVerificationCodesSheet = false;
			this.currentCouponForVerification = null;
		}
	},
	created() {
		if (!this.menuStore.loadedBusinessMenu && this.menuStore.currBusinessId !== null) {
			this.menuStore.loadMenu();
		}
		if (!this.roomsStore.rooms) {
			this.roomsStore.getBusinessRooms();
		}
		if (!this.couponsStore.businessCoupons) {
			this.couponsStore.getBusinessCoupons();
		}
	},
	watch: {
		'userStore.currBusinessId': function (newVal, oldVal) {
			if (newVal !== null && newVal !== oldVal) {
				this.menuStore.loadMenu();
				this.roomsStore.getBusinessRooms();
				this.couponsStore.getBusinessCoupons();
			}
		},
	}
}
</script>

<style scoped lang="scss">
.force-width {
	display: flex;
	justify-content: flex-end;
}
.coupons-container {
	max-width: 1400px;
	margin: 0 auto;

	.classic-card:deep(.card-body) {
		padding: 0 !important;

		& > .list-container {
			.list-item:first-child::before {
				display: none;
			}

			.list-item:last-child::after {
				display: none;
			}

		}
	}

	.list-item-title {
		padding-right: 8px;
	}
	.button-btn-slot {
		margin-left: auto;
	}

	.row-clickable {
		cursor: pointer;
	}

	.switches-container {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		flex-wrap: wrap;
		gap: 12px;
		width: 100%;
		.switch {
			cursor: default;
		}
	}

	.linked-list-container {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: flex-start;
		flex-wrap: wrap;
		gap: 12px;
		width: 100%;

		.linked-list-item {
			background-color: var(--color-bg-primary-2);
			padding: 4px 12px;
			border-radius: 13px;
			font-size: 13px;
			box-shadow: 0 3px 2px var(--color-box-shadow-1);
			max-width: 150px;
			@include truncateString();
		}

	}

	.coupon-info-container {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		min-height: 35px;

		.value {
			text-align: right;
			margin-right: 10px;
			font-size: 14px;
		}

		.usages {
			text-align: center;
			margin-right: 10px;
			background-color: $primary;
			color: var(--color-bg);
			padding: 3px 8px;
			border-radius: 13px;
			min-width: 40px;
			font-size: 14px;

			&.used {
				background-color: $danger;
			}
		}
	}

	.new-coupon-row {
		min-height: 35px;
		cursor: pointer;
		background-color: var(--color-bg-primary-2);
		padding: 5px 10px;
		border-radius: 13px;
		.chevron-container {
			@include centered;
			justify-content: flex-end;
			width: 50px;
			height: 35px;
		}
		.new-coupon-title {
			display: flex;
			align-items: center;
			justify-content: left;
			width: calc(100% - 50px);
			p {
				color: $primary;
			}
			.icon-plus {
				width: 14px;
				height: 14px;
				margin-right: 10px;
				display: flex;
				align-items: center;
				justify-content: center;

				&:deep(svg) {
					width: 100%;
					height: 100%;

					.fill {
						fill: var(--color-primary);
					}
				}
			}
		}
	}

	.extra-text {
		p {
			font-size: 14px;
			color: #9B9B9B;
		}
	}

	.coupon-value-switch {
		display: flex;
		align-items: flex-end;
		justify-content: flex-end;
	}

	@media (max-width: 600px) {
		.text-slot {
			flex-direction: column;
			align-items: flex-start;
			justify-content: center;
			gap: 4px;
		}

	}

	.ml-auto {
		margin-left: auto;
	}

	.coupon-mode-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		width: 100%;
		gap: 8px;
		padding-bottom: 8px;
	}

}
</style>